import {useEffect, useMemo, useRef, useState} from 'react';
import {asyncForEach, uniqueArray} from '@netx/core-next/utils/array';
import {
	AttributeTemplateObjectType,
	useAttributeProvider,
	type IAttributeTemplate,
	type Lookups,
	type CommonUseAttributeOptions,
	type AttributeTemplateType,
} from '@netx/core-attribute';
import useBackboneCollection from '@netx/core-components/hooks/useBackboneCollection';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {getAttributeFieldSet} from '../api/getAttributeFieldSet';
import type {IAttributeSet, AttributeSetId} from '../types';
import {useAttributeSets as useAttributeSetsStore} from '../store/attributeSets';

// TODO: move to util or to the attributes context
// The logic for these two things are identical, save the prop being checked
const getLookupThing = (
	lookups: Lookups,
	attributeTemplates: IAttributeTemplate[],
	thing: keyof Lookups,
) => {
	return lookups[thing].filter((key) => {
		const attr = attributeTemplates.find((attr) => attr.attributeid === key);
		const prop = thing === 'keys' ? 'keyDatasets' : 'targetDatasets';
		const otherProp = thing === 'keys' ? 'targetDatasets' : 'keyDatasets';
		return attr?.[prop].some((keyId) =>
			attributeTemplates.some((attr) => attr[otherProp].includes(keyId)),
		);
	});
};

export type UseAttributeSetsParams = {
	omitTypes?: AttributeTemplateType[];
	injectMandatory?: boolean;
	omitReadOnly?: boolean;
	objectType?: AttributeTemplateObjectType;
	limitToAttributeIds?: number[];
};

export const useAttributeSets = ({
	injectMandatory,
	limitToAttributeIds,
	omitReadOnly: _omitReadOnly,
	omitTypes: _omitTypes,
	objectType,
}: UseAttributeSetsParams) => {
	const attributesProvider = useAttributeProvider();

	// const [data, setData] = useState<IAttributeSet[]>([]);
	const {loading, attributeSets, filters} = useAttributeSetsStore();

	const userSetIdsRef = useRef<Record<AttributeSetId, number[]>>({});

	// TODO: until we can wrap the entire app in we just need to utilize the singleton
	// and provide some bridging between that and the context
	// const attributeSets = useBackboneCollection(attributeSetsSingleton, false);
	const attributeSetFilters: IAttributeSet[] = useBackboneCollection(
		filters,
		false,
	);

	useEffect(() => {
		userSetIdsRef.current = {};
	}, [attributeSets]);

	/** get an attribute set filter by attribute set id */
	const getFilterById = useConstCallback((id: AttributeSetId) =>
		attributeSetFilters.find((attrSet) => attrSet.id === id),
	);

	/** all attribute templates available in sets */
	const allAttributeTemplates = useMemo(() => {
		return attributesProvider.getByIds(
			attributeSets.find((d) => d.id === 'all')?.attributeIds || [],
			{
				omitReadOnly: _omitReadOnly,
				omitTypes: _omitTypes,
				limitToIds: limitToAttributeIds,
			},
		);
	}, [attributeSets, attributesProvider.data]);

	/** all attribute templates available in sets as an object indexed by attribute template id */
	const allAttributeTemplatesAsObject = useMemo(() => {
		return attributesProvider.getByIdsAsObject(
			attributeSets.find((d) => d.id === 'all')?.attributeIds || [],
			{
				omitReadOnly: _omitReadOnly,
				omitTypes: _omitTypes,
				limitToIds: limitToAttributeIds,
			},
		);
	}, [allAttributeTemplates]);

	/** all attribute template ids available in attribute sets */
	const allAttributeIds = useMemo(
		() => allAttributeTemplates.map((attr) => attr.attributeid),
		[allAttributeTemplates],
	);

	/** get attribute set by attribute set id */
	const getById = useConstCallback(
		(id: AttributeSetId, includeInactive = false) =>
			attributeSets.find(
				(attrSet) =>
					(attrSet.active !== 0 || includeInactive) && attrSet.id === id,
			) ??
			attributeSetFilters.find(
				(attrSet) =>
					(attrSet.active !== 0 || includeInactive) && attrSet.id === id,
			),
	);

	/** get attribute sets by attribute set ids */
	const getByIds = useConstCallback(
		(ids: AttributeSetId[], includeInactive = false) => {
			if (ids.length === 1 && ids[0] === 'custom') {
				return [
					{
						id: 'custom',
						name: 'Custom',
						attributeNames: [],
						attributeIds: [],
					},
				];
			}
			return attributeSets
				.filter(
					(attrSet) =>
						(attrSet.active !== 0 || includeInactive) &&
						ids.includes(attrSet.id),
				)
				.concat(
					attributeSetFilters.filter(
						(attrSet) =>
							(attrSet.active !== 0 || includeInactive) &&
							ids.includes(attrSet.id),
					),
				);
		},
	);

	// this is async due to user sets being shared resources
	// that need to be fetched in full to get the `attributeIds`
	const getAttributeTemplatesById = useConstCallback(
		async (id: AttributeSetId, options: CommonUseAttributeOptions = {}) => {
			const attributeSet = getById(id);
			if (!attributeSet) return [];

			const getTemplates = (attributeIds: number[]) => {
				const attributeSetAttribute = attributesProvider.getByIds(
					attributeIds,
					{
						...options,
						omitReadOnly: _omitReadOnly || options.omitReadOnly,
						omitTypes: _omitTypes || options.omitTypes,
						limitToIds: limitToAttributeIds || options.limitToIds,
					},
				);

				const mandatoryAttributes = attributesProvider
					.getAll({objectType: AttributeTemplateObjectType.ASSET})
					.filter((attr) => attr.mandatory);

				return injectMandatory
					? mandatoryAttributes.concat(
							attributeSetAttribute.filter((attr) => !attr.mandatory),
						)
					: attributeSetAttribute;
			};

			if (id.match(/^user-/)) {
				const attributeIds = (userSetIdsRef.current[id] = userSetIdsRef.current[
					id
				]
					? userSetIdsRef.current[id]
					: (
							await getAttributeFieldSet({
								id: parseInt(id.replace(/^user-/, '')),
							})
						).attributeIds);

				return getTemplates(attributeIds);
			}

			return getTemplates(attributeSet.attributeIds);
		},
	);

	/** get attribute templates by attribute set ids */
	const getAttributeTemplatesByIds = useConstCallback(
		async (ids: AttributeSetId[], options?: CommonUseAttributeOptions) => {
			let attributeTemplates: IAttributeTemplate[] = [];
			await asyncForEach(ids, async (id) => {
				attributeTemplates = attributeTemplates.concat(
					await getAttributeTemplatesById(id, options),
				);
			});
			return [...new Set(attributeTemplates)];
		},
	);

	/** get attribute template ids by attribute set id  */
	const getAttributeTemplateIdsById = useConstCallback(
		async (id: AttributeSetId, options?: CommonUseAttributeOptions) =>
			(await getAttributeTemplatesById(id, options)).map(
				(attr) => attr.attributeid,
			),
	);

	/** get attribute template ids by attribute set ids */
	const getAttributeTemplateIdsByIds = useConstCallback(
		async (ids: AttributeSetId[], options?: CommonUseAttributeOptions) =>
			(await getAttributeTemplatesByIds(ids, options)).map(
				(attr) => attr.attributeid,
			),
	);

	/** get metadata lookups by attribute set id */
	const getMetadataLookupsById = useConstCallback(
		async (id: AttributeSetId) => {
			const {lookups} = attributesProvider;
			const attributeTemplates = await getAttributeTemplatesById(id);
			return {
				keys: getLookupThing(lookups, attributeTemplates, 'keys'),
				targets: getLookupThing(lookups, attributeTemplates, 'targets'),
			};
		},
	);

	/** get metdata lookups by attribute set ids */
	const getMetadataLookupsByIds = useConstCallback(
		async (ids: AttributeSetId[]) => {
			const {lookups} = attributesProvider;
			const attributeTemplates = await getAttributeTemplatesByIds(ids);
			const mer = {
				keys: uniqueArray(getLookupThing(lookups, attributeTemplates, 'keys')),
				targets: uniqueArray(
					getLookupThing(lookups, attributeTemplates, 'targets'),
				),
			};
			return mer;
		},
	);

	/** get selectable attribute sets */
	const getSelectable = useConstCallback(
		(
			options: {
				ignoreSystemMandatory?: boolean;
				ignoreUserMandatory?: boolean;
			} = {},
		) => {
			const sets =
				options.ignoreSystemMandatory &&
				(options.ignoreUserMandatory ||
					!attributesProvider.hasMandatoryUserDefinedTemplates)
					? attributeSets.filter((d) => d.id !== 'mandatory')
					: attributeSets;

			// filter out system sets that do not have any available attributes
			// we do not have user set attribute ids until we fetch the full bean - at the start all we have are shared resources
			return sets.filter(
				(set) =>
					set.id != null &&
					(set.id.includes('user') ||
						attributesProvider.getByIds(set.attributeIds, {objectType}).length >
							0),
			);
		},
	);

	/** perform a lookup against the avaialble attribute sets */
	const lookup = useConstCallback(
		(
			attribute: IAttributeTemplate,
			lookupValue: string,
			options: {
				limitToIds?: number[];
			} = {},
		) => {
			return attributesProvider.lookup(attribute, lookupValue, {
				...options,
				limitToIds: options.limitToIds || allAttributeIds,
			});
		},
	);

	return {
		allAttributeTemplatesAsObject,
		allAttributeIds,
		allAttributeTemplates,
		totalAttributeTemplates: allAttributeTemplates.length,
		data: attributeSets,
		// user sets are async
		loading,
		// track loading of templates, sets cannot load templates until attributes are ready
		loadingTemplates: attributesProvider.loading,
		attributesCtx: attributesProvider,

		getAttributeTemplatesById,
		getAttributeTemplatesByIds,
		getAttributeTemplateIdsById,
		getAttributeTemplateIdsByIds,
		getById,
		getByIds,
		getFilterById,
		getMetadataLookupsById,
		getMetadataLookupsByIds,
		getSelectable,
		lookup,
	};
};

export default useAttributeSets;
